import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertCircle, ExternalLink } from "lucide-react";
import { useUsageLimitStore, type UsageType } from "@/stores/usageLimitStore";

const getUsageTypeDisplayName = (usageType: UsageType): string => {
  switch (usageType) {
    case 'searches':
      return 'searches';
    case 'batch_exports':
      return 'batch exports';
    case 'wallet_exports':
      return 'wallet exports';
    default:
      return 'usage';
  }
};

const getUsageTypeDescription = (usageType: UsageType): string => {
  switch (usageType) {
    case 'searches':
      return 'You have reached your monthly limit for blockchain searches (addresses, transactions, blocks).';
    case 'batch_exports':
      return 'You have reached your monthly limit for batch address export reports.';
    case 'wallet_exports':
      return 'You have reached your monthly limit for individual wallet address export reports.';
    default:
      return 'You have reached your monthly usage limit.';
  }
};

export function UsageLimitExceededDialog() {
  const { isOpen, usageType, current, limit, resetDate, hideModal } = useUsageLimitStore();
  const formatResetDate = (dateString?: string) => {
    if (!dateString) return "the 1st of next month";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (!usageType) return null;

  return (
    <Dialog open={isOpen} onOpenChange={hideModal}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                Usage Limit Reached
              </DialogTitle>
            </div>
          </div>
        </DialogHeader>
        
        <div className="space-y-4">
          <DialogDescription className="text-sm text-gray-600 dark:text-gray-400">
            {getUsageTypeDescription(usageType)}
          </DialogDescription>
          
          {/* Usage Stats */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Current Usage:
              </span>
              <span className="text-sm font-bold text-gray-900 dark:text-white">
                {current} / {limit}
              </span>
            </div>
            <div className="mt-2">
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-red-500 h-2 rounded-full" 
                  style={{ width: '100%' }}
                ></div>
              </div>
            </div>
          </div>
          
          {/* Reset Information */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <strong>Your limits will reset on {formatResetDate(resetDate)}.</strong>
            </p>
          </div>
          
          {/* Upgrade Information */}
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
            <p className="text-sm text-green-700 dark:text-green-300">
              <strong>Need higher monthly limits?</strong> Request an upgrade to be an Advanced User via the Feedback page.
            </p>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex justify-end flex-col sm:flex-row gap-2 sm:gap-3 pt-4">
          <Button
            variant="outline"
            onClick={hideModal}
            size="sm"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
